import * as THREE from 'three';
import { BoxGeometry, CylinderGeometry, Group, Mesh, MeshStandardMaterial, Object3D, Plane, Quaternion, Raycaster, TorusGeometry, Vector2, Vector3, AdditiveBlending, NormalBlending } from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { isDesktop, isMobile, isTablet } from 'Utils/MediaQueryUtils';
// events
const pointerUp = "pointerup";
const pointerDown = "pointerdown";
const pointerMove = "pointermove";
// virtual
const onPointer = "onpointer";

const getThemeColor = (variableName) => {
  const root = document.body;
  const styles = getComputedStyle(root);
  return styles.getPropertyValue(`--${variableName}`).trim();
};
const selectCHex = '#f00166';

// cartesian basis
const ex = new Vector3(1, 0, 0);
const ey = new Vector3(0, 1, 0);
const ez = new Vector3(0, 0, 1);

const gizmoMaterial = new THREE.MeshBasicMaterial({
  color: '#0000ff',
  transparent: true,
  opacity: 1,
  side: THREE.DoubleSide
});


let arrowRadius = 0.8;
if(isMobile() || isTablet()) arrowRadius = 1.2;
const arrowHeight = 10;
const arrowSegments = 64;
const getArrow = (radius, axis, angle) => {
  const arrow = new CylinderGeometry(0.0, arrowRadius + 2, arrowHeight / 2, arrowSegments);
  arrow.translate(0, arrowHeight / 2, 0);
  const base = new CylinderGeometry(arrowRadius, arrowRadius, arrowHeight, arrowSegments);
  //base.translate(0, radius, 0);
  const geometry = mergeGeometries([base, arrow]);
  const arrowCenterPointY = radius + arrowHeight / 2 + arrowRadius;
  geometry.translate(0, arrowCenterPointY || 0, 0);

  const mesh = new Mesh(geometry, gizmoMaterial.clone());
  const hitBoxGeometry = new CylinderGeometry(arrowRadius + 5, arrowRadius + 10, arrowHeight + 10, 8);
  const hitBoxMaterial = new THREE.MeshBasicMaterial({ visible: false });
  const hitBox = new Mesh(hitBoxGeometry, hitBoxMaterial);
  mesh.add(hitBox); 
  const rotationQuaternion = new Quaternion();
  rotationQuaternion.setFromAxisAngle(axis, angle);
  mesh.applyQuaternion(rotationQuaternion);
  mesh.name = "arrow";
  mesh.geometry.computeBoundsTree();
  return mesh;
}

const torusLength = 64;
const torusRadius = 10;
const radius2 = 1;
const getTorus = (radius, axis, angle) => {
  const geometry = new TorusGeometry(radius, radius2, torusRadius, torusLength, Math.PI * 2);

  const mesh = new Mesh(geometry, gizmoMaterial.clone());
  const rotationQuaternion = new Quaternion();
  rotationQuaternion.setFromAxisAngle(axis, angle);
  mesh.applyQuaternion(rotationQuaternion);
  mesh.name = "torus";
  return mesh;
}

class HelperGroup extends Group {
  constructor(radius) {
    super();
    this.radius = radius;
    this.startPointer = new Vector3();
    this.currentPointer = new Vector3();

    this.tangentDirection = new Vector3();// tangential vector
    this.radial = new Vector3();
    this.normal = new Vector3();
    this.workingQuaternion = new Quaternion();
    this.enabled = true;
  }

  select() {
    if (this.selected || !this.enabled) return;
    this.colorArr = [];
    this.children.forEach(_ => {
      this.colorArr.push(_.material.color.getHex());
      _.material.color.set(selectCHex);
    });
    this.selected = true;
  }

  unselect() {
    if (!this.selected || !this.enabled) return;
    this.children.forEach((_, index) => {
      _.material.color.setHex(this.colorArr[index]);
    });
    this.selected = false;
  }
}

class TorusGroup extends HelperGroup {
  constructor(radius) {
    super(radius);
    this.speed = 0.05;
    this.add(getTorus(radius, new Vector3(0, 0, 1), Math.PI / 2));
    this.name = "torusGroup";
  }

  move(pointerA, pointerB, camera, ipoint) {
    if (!this.enabled) return;
    if (!this.parent.parent) return;
    this.startPointer.copy(pointerA);
    this.currentPointer.copy(pointerB);

    // Decompose to get gizmo world position and orientation
    const decomposedQuaternion = new Quaternion();
    const objectPosition = new Vector3();
    this.matrixWorld.decompose(objectPosition, decomposedQuaternion, { x: 1, y: 1, z: 1 });

    // Rotation plane: normal is the gizmo's local Z axis in world space (torus normal)
    this.normal.copy(ez).applyQuaternion(decomposedQuaternion).normalize();
    const plane = new Plane().setFromNormalAndCoplanarPoint(this.normal, objectPosition);

    // Helper to intersect camera ray (through NDC pointer) with the rotation plane
    const intersectPlaneAtPointer = (ndcPointer) => {
      const worldPoint = new Vector3(ndcPointer.x, ndcPointer.y, ndcPointer.z).unproject(camera);
      const rayOrigin = camera.position.clone();
      const rayDir = worldPoint.sub(rayOrigin).normalize();
      const ray = new THREE.Ray(rayOrigin, rayDir);
      const hit = new Vector3();
      ray.intersectPlane(plane, hit);
      return hit;
    };

    // Compute successive hit points and vectors from gizmo center
    const hitA = intersectPlaneAtPointer(this.startPointer);
    const hitB = intersectPlaneAtPointer(this.currentPointer);
    if (!hitA || !hitB) return;

    const va = hitA.sub(objectPosition);
    const vb = hitB.sub(objectPosition);
    if (va.lengthSq() === 0 || vb.lengthSq() === 0) return;

    // Signed angle around plane normal using atan2 for full 360° support
    const cross = new Vector3().crossVectors(va, vb);
    const dot = va.dot(vb);
    const signed = this.normal.dot(cross);
    const delta = Math.atan2(signed, dot);

    this.workingQuaternion.setFromAxisAngle(this.normal, delta);
    this.parent.parent.applyQuaternion(this.workingQuaternion);

  }

  ensureRotationYMatch(angle) {
    const startForward = new THREE.Vector3(0, 0, 1).applyQuaternion(this.parent.parent.quaternion);
    const targetForward = new THREE.Vector3(0, 0, 1).applyAxisAngle(new THREE.Vector3(0, 1, 0), angle); // Y-axis rotation
    const rotationAxis = new THREE.Vector3().crossVectors(startForward, targetForward).normalize();
    const rotationAngle = Math.acos(startForward.dot(targetForward) / (startForward.length() * targetForward.length()));
    if (isNaN(rotationAngle) || rotationAngle === 0) {
      return; // No rotation needed
    } else {
      this.workingQuaternion.setFromAxisAngle(rotationAxis, rotationAngle);
      this.parent.parent.applyQuaternion(this.workingQuaternion);
      this.parent.parent.updateMatrixWorld(true);
    }
  }
}

class ArrowGroup extends HelperGroup {
  constructor(radius) {
    super(radius);
    this.speed = 1;
    this.add(getArrow(radius, new Vector3(0, 0, 1), Math.PI / 2));
    this.add(getArrow(radius, new Vector3(0, 0, 1), -Math.PI / 2));
    this.name = "arrowGroup";
  }

  move(pointerA, pointerB, camera) {
    this.startPointer.copy(pointerA);
    this.currentPointer.copy(pointerB);
    this.startPointer.unproject(camera);
    this.currentPointer.unproject(camera);
    this.currentPointer.sub(this.startPointer);

    this.workingQuaternion = new Quaternion();
    this.objectPosition = new Vector3();
    this.matrixWorld.decompose(this.objectPosition, this.workingQuaternion, { x: 1, y: 1, z: 1 });

    if (this.parent?.parent?.position){
      this.tangentDirection.copy(ex).applyQuaternion(this.workingQuaternion);
      const translationDelta = this.currentPointer.multiplyScalar(this.speed).length() * Math.sign(this.currentPointer.dot(this.tangentDirection));
      this.tangentDirection.multiplyScalar(translationDelta);
      this.parent.parent.position.add(this.tangentDirection);
    }
  }
}

class ScaleGroup extends HelperGroup {
  constructor(radius, position, axis) {
    super(radius);
    this.speed = 0.005;
    this.scaleFactor = 1.0;
    this.axis = axis;
    let color = '#f89a69';
    if (axis === 'x') {
      this.axisVector = ex;
    } else if (axis === 'y') {
      this.axisVector = ey;
    } else if (axis === 'z') {
      this.axisVector = ez;
    }

    // Create a cube geometry for scale handles - make them more visible
    const boxGeometry = new BoxGeometry(radius * 0.25, radius * 0.25, radius * 0.25);

    const scaleHandle = new Mesh(boxGeometry, gizmoMaterial.clone());
    scaleHandle.material.color.set(color);
    scaleHandle.position.copy(position);
    if (axis === 'x') {
      scaleHandle.position.x += 5;
      //scaleHandle.position.y -= (radius * 0.125);
      //scaleHandle.position.z -= (radius * 0.125);
    } else if (axis === 'y') {
      scaleHandle.position.y += 11;
    } else {
      scaleHandle.position.z += 5;
      //scaleHandle.position.x -= (radius * 0.125);
      //scaleHandle.position.y -= (radius * 0.125);
    }
    scaleHandle.name = "scaleHandle";
    this.add(scaleHandle);
    this.scaleHandleStartingPosition = scaleHandle.position.clone();
    this.name = "scaleGroup";
  }

  move(pointerA, pointerB, camera) {
    if (!this.enabled) return;
    if (!this.parent.parent) return;
    this.startPointer.copy(pointerA);
    this.currentPointer.copy(pointerB);
    this.startPointer.unproject(camera);
    this.currentPointer.unproject(camera);
    this.currentPointer.sub(this.startPointer);

    this.workingQuaternion = new Quaternion();
    this.objectPosition = new Vector3();
    this.matrixWorld.decompose(this.objectPosition, this.workingQuaternion, { x: 1, y: 1, z: 1 });

    this.tangentDirection.copy(this.axisVector).applyQuaternion(this.workingQuaternion);
    const scaleDelta = this.currentPointer.multiplyScalar(this.speed).length() * Math.sign(this.currentPointer.dot(this.tangentDirection));

    // Calculate scale factor based on movement - make it more sensitive
    this.scaleFactor = 1.0 + scaleDelta * 5; // Increased from 0.01 to 0.05

    // Apply scaling to the parent object - PER-AXIS SCALING
    const currentScale = this.parent.parent.scale;

    // Determine which axis this scale group controls
    if (this.axis === 'x') {
      currentScale.x *= this.scaleFactor;
    } else if (this.axis === 'y') {
      currentScale.y *= (1.0 + scaleDelta * 7);
    } else if (this.axis === 'z') {
      currentScale.z *= this.scaleFactor;
    }

    // Ensure scale doesn't go below 0.1 to prevent objects from disappearing
    currentScale.x = Math.max(0.5, Math.min(currentScale.x, 2));
    currentScale.y = Math.max(0.1, Math.min(currentScale.y, 10));
    currentScale.z = Math.max(0.5, Math.min(currentScale.z, 2));

    this.parent.scale.x = 1 / currentScale.x;
    this.parent.scale.y = 1 / currentScale.y;
    this.parent.scale.z = 1 / currentScale.z;

    const handle = this.getObjectByName('scaleHandle');
    const handlePosition = this.getObjectByName('scaleHandle').position.clone();
    const handlePositionDelta = scaleDelta * 150;
    if (this.axis === 'x') {
      handlePosition.x = this.calculateHandlePosition(
        handlePosition.x, handlePositionDelta, this.scaleHandleStartingPosition.x
      );
    } else if (this.axis === 'y') {
      handlePosition.y = this.calculateHandlePosition(
        handlePosition.y, handlePositionDelta, this.scaleHandleStartingPosition.y
      );
    } else if (this.axis === 'z') {
      handlePosition.z = this.calculateHandlePosition(
        handlePosition.z, handlePositionDelta, this.scaleHandleStartingPosition.z
      );
    }
    handle.position.copy(handlePosition);
  }

  calculateHandlePosition(handlePositionInAxis, handlePositionDelta, scaleHandleStartingPositionInAxis) {
    return Math.sign(scaleHandleStartingPositionInAxis) * Math.max(
        Math.min(
          Math.abs(handlePositionInAxis + handlePositionDelta), 
          Math.abs(scaleHandleStartingPositionInAxis * 2)
        ),
        Math.abs(0)
      );
  }

  resetPosition() {
    const scaleHandle = this.getObjectByName('scaleHandle');
    scaleHandle.position.copy(this.scaleHandleStartingPosition.clone().divide(this.scale));
  }

  resetScales() {
    this.scale.set(1, 1, 1);
    const scaleHandle = this.getObjectByName('scaleHandle');
    scaleHandle.scale.set(1, 1, 1);
    this.resetPosition();
  }
}

class TranslateGroup extends HelperGroup {
  constructor(radius) {
    super(radius);
    this.speed = 1;
    let color = '#0000ff';

    const boxGeometry = new THREE.SphereGeometry(2);
    // const boxGeometry = new THREE.CircleGeometry(2);
    const translateHandle = new Mesh(boxGeometry, gizmoMaterial.clone());
    translateHandle.material.color.set(color);
    translateHandle.position.set(0, 0, 0);
    translateHandle.name = "translateHandle";
    this.add(translateHandle);

    this.name = "translateGroup";
  }

  move(pointerA, pointerB, camera) {
    this.startPointer.copy(pointerA);
    this.currentPointer.copy(pointerB);
    this.startPointer.unproject(camera);
    this.currentPointer.unproject(camera);
    this.currentPointer.sub(this.startPointer);

    this.workingQuaternion = new Quaternion();
    this.objectPosition = new Vector3();
    this.matrixWorld.decompose(this.objectPosition, this.workingQuaternion, { x: 1, y: 1, z: 1 });

    if (this.parent?.parent?.position){
      this.tangentDirection.copy(new Vector3(1, 0, 1));
      const translationDelta = this.currentPointer.multiplyScalar(this.speed);
      this.tangentDirection.multiply(translationDelta);
      this.parent.parent.position.add(this.tangentDirection);
    }
  }
}

class ControlEvents extends Object3D {
  constructor(camera, renderer) {
    super();
    this.debug = false;
    this.camera = camera;
    this.renderer = renderer;
    this.renderer.localClippingEnabled = false;
    this.domElement = renderer.domElement;
    this.domElement.style.touchAction = 'none'; // disable touch scroll

    this.va = new Vector2();
    this.vb = new Vector2();

    this.workingQuaternion = new Quaternion();
    this.p = new Vector3();
    this.n = new Vector3();
    this.plane = new Plane(new Vector3(0, 0, 1.0), 0.0);

    this.raycaster = new Raycaster();
    this.getPointer = this.getPointer.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onTap = this.onTap.bind(this);
    this.update = this.update.bind(this);
    this.dragging = false;
    this.activeControl = null;
    this.init();
  }

  getDelta(pointerA, pointerB) {
    this.va.x = pointerA.x;
    this.va.y = pointerA.y;
    this.vb.x = pointerB.x;
    this.vb.y = pointerB.y;
    this.vb.sub(this.va);
    return this.vb.length();
  }

  getScreenZ(camera) {
    if (camera.isPerspectiveCamera) {
      let e = camera.matrixWorldInverse.elements;
      let z = e[14] / e[15];
      e = camera.projectionMatrix.elements;
      return (e[10] * z + e[14]) / (e[11] * z + e[15]);
    } else if (camera.isOrthographicCamera) {
      return (camera.near + camera.far) / (camera.near - camera.far);
    }
  }

  getPointer(e) {
    if (this.domElement.ownerDocument.pointerLockElement) {
      return {
        x: 0.0,
        y: 0.0,
        z: this.getScreenZ(this.camera),
        button: e.button
      };
    } else {
      const rect = this.domElement.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / rect.width * 2 - 1,
        y: - (e.clientY - rect.top) / rect.height * 2 + 1,
        // for setFromCamera have no effect because setFromCamera( has coords.x and coords.y only ...)
        // but for unproject z is strongly! see move proc
        z: this.getScreenZ(this.camera),
        button: e.button
      };
    }
  }

  onPointerDown(e) {
    if (this.debug) console.log("onPointerDown");
    this.isTap = true;
    if (!document.pointerLockElement) {
      this.domElement.setPointerCapture(e.pointerId);
    }
    const pointer = this.getPointer(e);
    this.startPointer = pointer;
    if (this.intersect(pointer)) {
      this.dragging = true;
      this.activeControl = this.current;
      this.domElement.addEventListener('pointermove', this.onPointerMove);
      this.domElement.addEventListener('pointerup', this.onPointerUp);

      this.dispatchEvent({ type: onPointer, onChange: true });
    }
  }

  onPointerUp(e) {
    if (this.debug) console.log("onPointerUp");
    this.startPointer = undefined;
    this.ipoint = undefined;
    this.domElement.releasePointerCapture(e.pointerId);
    this.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.domElement.removeEventListener('pointerup', this.onPointerUp);
    this.dragging = false;
    this.activeControl = null;
    this.dispatchEvent({ type: onPointer, onChange: false });
    if (!this.isTap) {
      this.hoverClear();
      return;
    }
    // event on Tap!!!
    this.onTap(e);

  }

  onMouseMove(e) {
    switch (e.pointerType) {
      case 'mouse':
        // case 'pen':
        if (e.pressure === 0) {
          if (!this.dragging) {
            const pointer = this.getPointer(e);
            this.intersect(pointer);
          }
        } else {
          // this.dispatchEvent({ type: onPointer, isPressed: false });
        }
        this.isTap = false;
        break;
    }
  }

  onPointerMove(e) {
    switch (e.pointerType) {
      case 'mouse':
      case 'touch':
        const pointer = this.getPointer(e);

        this.activeControl &&
        this.activeControl.move &&
        this.activeControl.move(
          this.startPointer,
          pointer,
          this.camera,
          this.ipoint
        );

        this.startPointer = pointer;

        if (this.debug) console.log("onPointerMove");

        this.isTap = false;

        break;
    }
  }


  onTap(e) {
    if (this.debug) console.log("onTap");
  }

  intersect(pointer) {
    this.raycaster.setFromCamera(pointer, this.camera);
    const intersect = this.raycaster.intersectObjects(this.children, true);
    if (intersect.length > 0) {
      const items = intersect;
      this.ipoint = intersect[0].point;
      this.hover(items);
      return true;
    } else {
      if (!this.dragging) {
        this.hoverClear();
      }
      return false;
    }

  }

  hover(items) {
    let item = items[0].object;

    while (item && !item.select) {
      item = item.parent;
    }

    if (item && item.select) {
      if (this.current !== item) {
        this.hoverClear();
        item.select();
        this.current = item;
      }
    }
  }

  hoverClear() {
    this.current && this.current.unselect && this.current.unselect();
    this.current = undefined;
  }

  init() {
    this.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.domElement.addEventListener('pointermove', this.onMouseMove);
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.domElement.removeEventListener('pointermove', this.onMouseMove);
  }

  update() {
    this.n.set(0, 0, 1);
    this.n.applyQuaternion(this.camera.quaternion);
    this.plane.normal.copy(this.n);
    const s = { x: 1, y: 1, z: 1 };
    this.matrixWorld.decompose(this.p, this.workingQuaternion, s);
    this.plane.constant = - this.n.dot(this.p);
    this.torusN && this.torusN.quaternion.copy(this.camera.quaternion);
    this.torusN && this.torusN.applyQuaternion(this.workingQuaternion.invert());
  }

}

class AtomiControls extends ControlEvents {
  constructor(camera, renderer, radius) {
    super(camera, renderer);
    const rotationQuaternion = new Quaternion();

    this.radius = radius || 0.2;

    const torusY = new TorusGroup(this.radius);
    rotationQuaternion.setFromAxisAngle(ex, Math.PI / 2);
    torusY.applyQuaternion(rotationQuaternion);
    torusY.name = "torusY";
    this.add(torusY);

    const arrowX = new ArrowGroup(this.radius);
    arrowX.name = "arrowX";
    this.add(arrowX);

    arrowX.updateMatrixWorld(true);
    const arrowX_BB = new THREE.Box3().setFromObject(arrowX);
    const scaleXPosition = arrowX_BB.max.clone();
    scaleXPosition.x -= 8;
    scaleXPosition.z -= 11;


    const arrowZ = new ArrowGroup(this.radius);
    rotationQuaternion.setFromAxisAngle(ey, Math.PI / 2);
    arrowZ.applyQuaternion(rotationQuaternion);
    arrowZ.name = "arrowZ";
    this.add(arrowZ);

    arrowZ.updateMatrixWorld(true);
    const arrowZ_BB = new THREE.Box3().setFromObject(arrowZ);
    const scaleZPosition = arrowZ_BB.max.clone();
    scaleZPosition.x -= 11;
    scaleZPosition.z -= 8;


    // Add scale controls
    const scaleX = new ScaleGroup(this.radius, scaleXPosition, 'x');
    scaleX.name = "scaleX";
    this.add(scaleX);

    const scaleY = new ScaleGroup(this.radius, new Vector3(0, 0, 0), 'y');
    scaleY.name = "scaleY"; 
    this.add(scaleY);

    const scaleZ = new ScaleGroup(this.radius, scaleZPosition, 'z');
    scaleZ.name = "scaleZ";
    this.add(scaleZ);

    // Add 2D translation controls
    const translateGroup = new TranslateGroup(this.radius);
    rotationQuaternion.setFromAxisAngle(ex, Math.PI / 2);
    translateGroup.applyQuaternion(rotationQuaternion);
    this.add(translateGroup);

    // Ensure gizmo draws last and never occludes background
    this.renderOrder = 999;
    this.traverse(child => {
      if (child.isMesh && child.material) {
        child.renderOrder = 999;
        child.material.depthWrite = false;
        child.material.depthTest = false;
        // Don't override transparent or blending here so per-handle settings apply
      }
    });
  }

  hover(items) {
    super.hover(items);
  }

  hoverClear() {
    super.hoverClear();
  }

  resetScalePointersPosition() {
    this.children.forEach(child => {
      if (child instanceof ScaleGroup){
        child.resetPosition();
      }
    });
  }

  resetScales() {
    this.parent?.traverse((child) => {
      child.scale.set(1, 1, 1);
      if (child instanceof ScaleGroup){
        child.resetPosition();
      }
    });
  }

  ensureRotationMatch(angle){
    this.children.forEach(child => {
      if (child instanceof TorusGroup) {
        child.ensureRotationYMatch(angle);
      }
    });
  }

  limit() {
    this.children.forEach(child => {
      if (child instanceof ScaleGroup) {
        child.enabled = false;
        child.visible = false;
      } else if (child instanceof TorusGroup) {
        child.enabled = false;
      }
    });
  }

  limitScale(axisLetter) { // axisLetter = 'x' or 'y' or 'z'
    this.children.forEach(child => {
      if (child instanceof ScaleGroup) {
        if (child.name === `scale${axisLetter.toUpperCase()}`) {
          child.enabled = false;
          child.visible = false;
        } else {
          child.enabled = true;
          child.visible = true;
        }
      }
    });
  }

  removeLimit() {
    this.children.forEach(child => {
      if (child instanceof ScaleGroup) {
        child.enabled = true;
        child.visible = true;
      } else if (child instanceof TorusGroup) {
        child.enabled = true;
      }
    });
  }

}

export { AtomiControls };